// Equation review screen (PRD 07 §9.4).
//
// Per-equation card: summary editor (two-register), interactions editor,
// per-symbol row with accept/edit/reject + grounding indicator. Save
// debounces via TanStack mutation; publish atomically promotes.

import { useMemo, useState } from "react";
import type {
  Definition,
  EquationEntry,
  EquationSymbol,
} from "../../data/types";
import {
  type EquationReviewAction,
  type EquationReviewState,
  type SymbolReviewAction,
  useEquationsReview,
  usePublishEquations,
  useSaveEquationsReview,
} from "./api";

import "./equations-review.css";

interface Props {
  docId: string;
  actor?: string;
  onPublished?: (publishedAt: string) => void;
}

function nowIso(): string {
  return new Date().toISOString();
}

export function EquationsReviewPage({
  docId,
  actor = "anonymous",
  onPublished,
}: Props) {
  const { data, isLoading, error } = useEquationsReview(docId);
  const save = useSaveEquationsReview(docId);
  const publish = usePublishEquations(docId);
  const [draft, setDraft] = useState<Record<string, EquationReviewAction>>({});

  const equations: EquationEntry[] = useMemo(
    () => data?.proposed.equations ?? [],
    [data],
  );

  if (isLoading) {
    return (
      <div className="maisight-equations-review-loading" role="status">
        Loading equations…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="maisight-equations-review-error" role="alert">
        Failed to load equations: {error?.message ?? "no data"}
      </div>
    );
  }
  if (equations.length === 0) {
    return (
      <div className="maisight-equations-review-empty">
        <h2>No equations to review</h2>
        <p>This doc has zero equation blocks. Nothing to publish.</p>
      </div>
    );
  }

  const persistedDraft: Record<string, EquationReviewAction> = {
    ...(data.review_state?.equations ?? {}),
    ...draft,
  };

  const setEquation = (
    eqId: string,
    patch: Partial<EquationReviewAction>,
  ) => {
    setDraft((d) => {
      const prev: EquationReviewAction =
        d[eqId] ??
        persistedDraft[eqId] ?? {
          action: "accepted",
          actor,
          at: nowIso(),
          symbols: {},
        };
      const next: EquationReviewAction = {
        ...prev,
        ...patch,
        actor,
        at: nowIso(),
      };
      return { ...d, [eqId]: next };
    });
  };

  const setSymbol = (
    eqId: string,
    symbolId: string,
    patch: Partial<SymbolReviewAction>,
  ) => {
    const current = persistedDraft[eqId];
    const existing: SymbolReviewAction =
      current?.symbols?.[symbolId] ?? {
        action: "accepted",
        actor,
        at: nowIso(),
      };
    setEquation(eqId, {
      symbols: {
        ...(current?.symbols ?? {}),
        [symbolId]: {
          ...existing,
          ...patch,
          actor,
          at: nowIso(),
        },
      },
    });
  };

  const handleSave = () => {
    const body: EquationReviewState = {
      doc_id: docId,
      extraction_equation_version: data.proposed.extraction_equation_version,
      equations: persistedDraft,
      saved_at: nowIso(),
    };
    save.mutate(body);
  };

  const handlePublish = async () => {
    handleSave();
    const result = await publish.mutateAsync(actor);
    const publishedAt = (result as { published_at?: string }).published_at;
    if (publishedAt) onPublished?.(publishedAt);
  };

  return (
    <div className="maisight-equations-review">
      <header className="maisight-equations-review-header">
        <h1>Equations — {docId}</h1>
        <p>
          Two registers per symbol. Reject anything ungrounded the model
          guessed at. Publish atomically promotes the entire set.
        </p>
        <div className="maisight-equations-review-actions">
          <button onClick={handleSave} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save draft"}
          </button>
          <button
            onClick={handlePublish}
            disabled={publish.isPending}
            className="maisight-equations-publish"
          >
            {publish.isPending ? "Publishing…" : "Publish"}
          </button>
        </div>
        {(save.error || publish.error) && (
          <p role="alert" className="maisight-equations-review-error-msg">
            {(save.error ?? publish.error)?.message}
          </p>
        )}
      </header>

      <ul className="maisight-equations-list">
        {equations.map((eq) => (
          <li key={eq.equation_id}>
            <EquationCard
              equation={eq}
              draft={persistedDraft[eq.equation_id]}
              onEquationChange={(patch) =>
                setEquation(eq.equation_id, patch)
              }
              onSymbolChange={(sid, patch) =>
                setSymbol(eq.equation_id, sid, patch)
              }
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

interface EquationCardProps {
  equation: EquationEntry;
  draft?: EquationReviewAction;
  onEquationChange: (patch: Partial<EquationReviewAction>) => void;
  onSymbolChange: (
    symbolId: string,
    patch: Partial<SymbolReviewAction>,
  ) => void;
}

function EquationCard({
  equation,
  draft,
  onEquationChange,
  onSymbolChange,
}: EquationCardProps) {
  const summary = draft?.edited_summary ?? equation.summary;
  const interactions =
    draft?.edited_interactions ?? equation.interactions ?? "";
  const action = draft?.action ?? "accepted";

  return (
    <article
      className="maisight-equation-card"
      data-action={action}
      data-equation-id={equation.equation_id}
    >
      <header>
        <code className="maisight-equation-card-latex">{equation.latex}</code>
        <ActionToggle
          value={action}
          onChange={(next) => onEquationChange({ action: next })}
        />
      </header>

      {summary && (
        <DefinitionEditor
          definition={summary}
          onChange={(next) => onEquationChange({ edited_summary: next })}
        />
      )}

      <label className="maisight-equation-interactions-editor">
        <span>Interactions</span>
        <textarea
          value={interactions}
          rows={2}
          onChange={(e) =>
            onEquationChange({ edited_interactions: e.target.value })
          }
        />
      </label>

      <ul className="maisight-equation-card-symbols">
        {equation.symbols.map((sym) => (
          <li key={sym.symbol_id}>
            <SymbolEditor
              symbol={sym}
              draft={draft?.symbols?.[sym.symbol_id]}
              onChange={(patch) => onSymbolChange(sym.symbol_id, patch)}
            />
          </li>
        ))}
      </ul>
    </article>
  );
}

interface ActionToggleProps {
  value: "accepted" | "edited" | "rejected" | "pending";
  onChange: (next: "accepted" | "edited" | "rejected") => void;
}

function ActionToggle({ value, onChange }: ActionToggleProps) {
  return (
    <div role="radiogroup" className="maisight-action-toggle">
      {(["accepted", "edited", "rejected"] as const).map((opt) => (
        <button
          key={opt}
          role="radio"
          aria-checked={value === opt}
          className={value === opt ? "is-on" : ""}
          onClick={() => onChange(opt)}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

interface SymbolEditorProps {
  symbol: EquationSymbol;
  draft?: SymbolReviewAction;
  onChange: (patch: Partial<SymbolReviewAction>) => void;
}

function SymbolEditor({ symbol, draft, onChange }: SymbolEditorProps) {
  const definition: Definition | null =
    draft?.edited_definition ?? symbol.definition ?? null;
  const action = draft?.action ?? "accepted";
  return (
    <div
      className="maisight-symbol-editor"
      data-action={action}
      data-grounding={symbol.grounding ?? ""}
    >
      <header>
        <code>{symbol.latex}</code>
        <span className="maisight-symbol-role">{symbol.role ?? "—"}</span>
        {symbol.grounding === "ungrounded" && (
          <span className="maisight-symbol-warning">
            ⚠ ungrounded
          </span>
        )}
        <ActionToggle
          value={action}
          onChange={(next) => onChange({ action: next })}
        />
      </header>
      {definition ? (
        <DefinitionEditor
          definition={definition}
          onChange={(next) =>
            onChange({ edited_definition: next, action: "edited" })
          }
        />
      ) : (
        <p className="maisight-symbol-stub">(no definition yet)</p>
      )}
    </div>
  );
}

interface DefinitionEditorProps {
  definition: Definition;
  onChange: (next: Definition) => void;
}

function DefinitionEditor({ definition, onChange }: DefinitionEditorProps) {
  return (
    <div className="maisight-definition-editor">
      <label>
        <span>Plain (≤200 chars)</span>
        <textarea
          value={definition.definition_plain}
          rows={2}
          onChange={(e) =>
            onChange({ ...definition, definition_plain: e.target.value })
          }
        />
      </label>
      <label>
        <span>Precise (≤350 chars)</span>
        <textarea
          value={definition.definition_precise}
          rows={2}
          onChange={(e) =>
            onChange({ ...definition, definition_precise: e.target.value })
          }
        />
      </label>
    </div>
  );
}
